import { z } from 'zod';

/**
 * First-touch ad click attribution (HAL-1969).
 *
 * The browser captures this on a halcy.ai page load that carries an ad click
 * ID or a UTM parameter, keeps it for 30 days, and sends it with the Discovery
 * Report create-check request. It is stored verbatim in
 * `ai_visibility_checks.attribution`, and the paid-ads outcome source reads it
 * from there through `leads.check_id` to tie a report lead to its campaign.
 *
 * Keys are snake_case and the nine URL parameters keep their query-string
 * names exactly, so a SQL reader writes `attribution->>'gclid'` and
 * `attribution->>'utm_campaign'` with no mapping.
 *
 * Strict: an unknown key fails, and every string is bounded, because this
 * object arrives from an unauthenticated public endpoint.
 */

/** Longest stored URL parameter value; a longer one is dropped at capture, never truncated. */
export const AD_ATTRIBUTION_PARAM_MAX_LENGTH = 500;

/** Longest stored landing path or referrer. */
export const AD_ATTRIBUTION_URL_MAX_LENGTH = 2048;

const paramValue = (description: string): z.ZodOptional<z.ZodString> =>
  z.string().min(1).max(AD_ATTRIBUTION_PARAM_MAX_LENGTH).optional().describe(description);

export const AdClickAttributionSchema = z
  .strictObject({
    gclid: paramValue('Google Ads click ID from the landing URL'),
    gbraid: paramValue('Google Ads click ID for app-conversion measurement on iOS 14+ clicks, from the landing URL'),
    wbraid: paramValue('Google Ads click ID for web-conversion measurement on iOS 14+ clicks, from the landing URL'),
    fbclid: paramValue('Meta click ID from the landing URL; Meta `fbc` is built from it and `clicked_at`'),
    utm_source: paramValue('`utm_source` from the landing URL'),
    utm_medium: paramValue('`utm_medium` from the landing URL'),
    utm_campaign: paramValue('`utm_campaign` from the landing URL; the paid-ads readout joins leads to ad cells on it'),
    utm_content: paramValue('`utm_content` from the landing URL'),
    utm_term: paramValue('`utm_term` from the landing URL'),
    landing_path: z
      .string()
      .min(1)
      .max(AD_ATTRIBUTION_URL_MAX_LENGTH)
      .startsWith('/')
      .describe('Pathname of the page the click landed on, without the query string'),
    referrer: z
      .string()
      .min(1)
      .max(AD_ATTRIBUTION_URL_MAX_LENGTH)
      .optional()
      .describe('`document.referrer` on the landing page load; absent when the browser sent none'),
    clicked_at: z
      .iso.datetime()
      .describe('ISO 8601 UTC time the landing page carrying the parameters loaded, which is the click time to within page-load latency'),
  })
  .describe('First-touch ad click attribution captured in the browser and stored on ai_visibility_checks.attribution');

export type AdClickAttribution = z.infer<typeof AdClickAttributionSchema>;

/** The URL query parameters whose presence starts a capture, in storage-key form. */
export const AD_ATTRIBUTION_URL_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const satisfies readonly (keyof AdClickAttribution)[];
